import { create } from "zustand";
import type { OccupancyCell } from "../scene/OccupancyLayer";
import type { ZoneRow } from "../api/rest";
import { EMPTY_FILTERS, type ProductFilters } from "../scene/productSearch";

export type CameraPreset = "iso" | "top" | "aisle" | "dock";

interface UIState {
  layers: { staleRobots: boolean; sensors: boolean; alerts: boolean; labels: boolean; trail: boolean; zones: boolean; heatmap: boolean; spaghetti: boolean; occupancy: boolean; rackVolumes: boolean; liveInventory: boolean; decks: boolean; minimap: boolean; kpiCoverage: boolean; kpiOccupancy: boolean; kpiVertical: boolean; kpiAccuracy: boolean };
  /** ADR 192: fleet visibility is per asset TYPE (rows generated from the
   *  roster), replacing the hardcoded robots/drones toggles. Absence = visible,
   *  so a type never seen before needs no entry to show up. */
  hiddenAssetTypes: Record<string, boolean>;
  cameraPreset: CameraPreset;
  /** ADR 369: first-person walk replaces the orbit rig while on. */
  walkMode: boolean;
  /** Top of the racking of a warehouse, metres — written by the rack layer when
   *  its volumes land, read by the walk as its ceiling. Keyed so a stale value
   *  from the previous warehouse is never applied to the next. */
  structureTop: { warehouseId: string; topM: number } | null;
  editMode: boolean;
  /** Sidebar rail: true/false = explicit user choice (persisted); null = auto (collapsed on /live only). */
  navCollapsed: boolean | null;
  /** ADR 190: the storage bin picked on the /live scene (occupancy cell click) —
   *  drives the InspectorPanel's bin branch. Cleared on pointer-miss, on layer
   *  teardown, and when an asset is selected instead. */
  selectedBin: OccupancyCell | null;
  /** ADR 278: the zone picked on the /live scene (zone-polygon click) — drives
   *  the bottom inspection dock. Holds the whole row (like selectedBin) so the
   *  dock never refetches zones. Cleared on pointer-miss, on ZonesLayer
   *  teardown, and by the dock's close button. */
  selectedZone: ZoneRow | null;
  /** ADR 280: the picked zone's WORLD-space AABB [minX,minY,minZ,maxX,maxY,maxZ],
   *  captured at click time from the zone mesh (it rides the warehouse layout
   *  transform, so store coordinates would be wrong). Lives and dies with
   *  selectedZone — both are written by the one setter so they cannot drift.
   *
   *  ADR 294: it is a FACT about the selection, no longer a camera command.
   *  Nothing reads it today; click time is the only cheap place to compute it,
   *  so it is kept for the explicit "frame this aisle" button. */
  zoneFocusBox: number[] | null;
  /** ADR 190: bumped after a write that reshapes scene data (height edit) — the
   *  occupancy/rack containers depend on it to refetch without a remount. */
  sceneDataVersion: number;
  /** ADR 196: the /live product search text. Matching happens where the cells
   *  live (OccupancyLayerContainer); this is only the query as typed. */
  productQuery: string;
  /** ADR 203: the finder's WMS-attribute predicates (dwell/expiry/mfg), stored
   *  as typed — raw strings, "" = off. Parsing/validation lives with the
   *  matcher, so a half-typed value is simply inactive, never an error. */
  productFilters: ProductFilters;
  /** Matched-address count for the search box, written by the occupancy
   *  container (the cells' owner). Null = search inactive or layer not mounted. */
  productMatchCount: number | null;
  /** The matched addresses themselves, kept in lockstep with the count (one
   *  setter writes both). The relocation simulator consumes this as its
   *  selection (ADR 351) — subscribers wanting cheap re-renders should watch
   *  productMatchCount and read this via getState() at submit time. */
  productMatches: string[] | null;
  /** A movement picked in the run-selection list (ADR 198): the scene marks the
   *  origin bin in amber beside the destination the product search already
   *  isolates. Cleared by any other search input or run-selection change — the
   *  origin is only meaningful for the product and run pair it was picked on. */
  movementFocus: { product: string; fromAddress: string; toAddress: string } | null;
  /** ADR 354/361/375: what the occupancy paint encodes — scan status (default),
   *  the Validade expiry ramp, the Idade dwell ramp (reversed colors), or the
   *  WMS comparison classes. A mode, not a layer: one story at a time. */
  paintBy: "status" | "expiry" | "aging" | "wms";
  /** ADR 356: diagnostic reveal mode — gated surfaces render a ghost with their
   *  requirement checklist instead of nothing. Toggled from the top bar by
   *  site/global admins only; deliberately NOT persisted (an investigation
   *  tool left on would haunt every later session with ghost cards). */
  revealMode: boolean;
  /** ADR 354/361 legend tallies for the active paint mode, written by the
   *  occupancy container (the cells' owner, like productMatches). Null =
   *  layer not mounted / no run painted. */
  paintCounts: import("../scene/expiryPaint").PaintCounts | null;
  /** ADR 198: which inventory runs paint the scene. Null = default (latest
   *  paintable run only). Keyed by warehouse so a stale selection never leaks
   *  into another warehouse's scene — consumers ignore it unless the ids match
   *  the active warehouse. */
  runSelection: { warehouseId: string; runIds: string[] } | null;
  /** ADR 290: the active relocation what-if — the scene paints its routes and
   *  emphasizes its destination zone. Warehouse-keyed like runSelection so a
   *  result never leaks into another warehouse's scene. */
  simulation: { warehouseId: string; destinationZoneId: string; result: import("../api/rest").RelocationSim } | null;
  toggleLayer: (k: keyof UIState["layers"]) => void;
  toggleAssetType: (typeCode: string) => void;
  setCameraPreset: (p: CameraPreset) => void;
  setWalkMode: (on: boolean) => void;
  setStructureTop: (warehouseId: string, topM: number | null) => void;
  toggleEditMode: () => void;
  setNavCollapsed: (v: boolean) => void;
  setSelectedBin: (b: OccupancyCell | null) => void;
  setSelectedZone: (z: ZoneRow | null, focusBox?: number[] | null) => void;
  bumpSceneData: () => void;
  setProductQuery: (q: string) => void;
  setProductFilters: (patch: Partial<ProductFilters>) => void;
  clearProductSearch: () => void;
  setProductMatches: (addresses: string[] | null) => void;
  setMovementFocus: (focus: { product: string; fromAddress: string; toAddress: string } | null) => void;
  setPaintBy: (mode: UIState["paintBy"]) => void;
  toggleRevealMode: () => void;
  setPaintCounts: (counts: import("../scene/expiryPaint").PaintCounts | null) => void;
  setRunSelection: (warehouseId: string, runIds: string[]) => void;
  setSimulation: (warehouseId: string, destinationZoneId: string, result: import("../api/rest").RelocationSim) => void;
  clearSimulation: () => void;
}

const NAV_COLLAPSED_KEY = "twin.navCollapsed";

function readStoredNavCollapsed(): boolean | null {
  try {
    const v = window.localStorage.getItem(NAV_COLLAPSED_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

export const useUIStore = create<UIState>((set) => ({
  // staleRobots off by default: forklifts not seen within STALE_MS are hidden until enabled.
  // occupancy/rackVolumes default ON (ADR 170/190): an inventory site shows its
  // racks and its latest run's goods on open; each renders nothing where there
  // is no structure / no run yet.
  // kpiCoverage/kpiOccupancy default OFF (ADR 193): badges are an opt-in reading
  // overlay, not part of the scene's resting state.
  layers: { staleRobots: false, sensors: true, alerts: true, labels: true, trail: true, zones: true, heatmap: false, spaghetti: false, occupancy: true, rackVolumes: true, liveInventory: true, decks: true, minimap: true, kpiCoverage: false, kpiOccupancy: false, kpiVertical: false, kpiAccuracy: false },
  hiddenAssetTypes: {},
  cameraPreset: "iso",
  walkMode: false,
  structureTop: null,
  editMode: false,
  navCollapsed: readStoredNavCollapsed(),
  selectedBin: null,
  selectedZone: null,
  zoneFocusBox: null,
  sceneDataVersion: 0,
  productQuery: "",
  productFilters: EMPTY_FILTERS,
  productMatchCount: null,
  productMatches: null,
  movementFocus: null,
  paintBy: "status",
  revealMode: false,
  paintCounts: null,
  runSelection: null,
  simulation: null,
  toggleLayer(k) { set((s) => ({ layers: { ...s.layers, [k]: !s.layers[k] } })); },
  toggleAssetType(typeCode) {
    set((s) => ({ hiddenAssetTypes: { ...s.hiddenAssetTypes, [typeCode]: !s.hiddenAssetTypes[typeCode] } }));
  },
  // A preset is an orbit pose; picking one while walking ends the walk.
  setCameraPreset(p) { set({ cameraPreset: p, walkMode: false }); },
  setWalkMode(on) { set({ walkMode: on }); },
  setStructureTop(warehouseId, topM) { set({ structureTop: topM == null ? null : { warehouseId, topM } }); },
  toggleEditMode() { set((s) => ({ editMode: !s.editMode })); },
  setSelectedBin(b) { set({ selectedBin: b }); },
  setSelectedZone(z, focusBox) { set({ selectedZone: z, zoneFocusBox: z ? focusBox ?? null : null }); },
  bumpSceneData() { set((s) => ({ sceneDataVersion: s.sceneDataVersion + 1 })); },
  setProductQuery(q) { set((s) => ({ productQuery: q, movementFocus: s.movementFocus?.product === q ? s.movementFocus : null })); },
  setProductFilters(patch) { set((s) => ({ productFilters: { ...s.productFilters, ...patch } })); },
  clearProductSearch() { set({ productQuery: "", productFilters: EMPTY_FILTERS, movementFocus: null }); },
  setProductMatches(addresses) {
    set({ productMatches: addresses, productMatchCount: addresses ? addresses.length : null });
  },
  setPaintBy(mode) { set({ paintBy: mode }); },
  toggleRevealMode() { set((s) => ({ revealMode: !s.revealMode })); },
  setPaintCounts(counts) { set({ paintCounts: counts }); },
  setRunSelection(warehouseId, runIds) { set({ runSelection: { warehouseId, runIds }, movementFocus: null }); },
  setMovementFocus(focus) { set({ movementFocus: focus }); },
  setSimulation(warehouseId, destinationZoneId, result) { set({ simulation: { warehouseId, destinationZoneId, result } }); },
  clearSimulation() { set({ simulation: null }); },
  setNavCollapsed(v) {
    try { window.localStorage.setItem(NAV_COLLAPSED_KEY, v ? "1" : "0"); } catch { /* storage unavailable */ }
    set({ navCollapsed: v });
  },
}));
